import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LoadingScreen.css';

/* ── 6 hand-drawn SVG eye frames ── */
const EyeFrame0 = () => (
  // CLOSED — two flat horizontal lines
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
    <line x1="20" y1="40" x2="80" y2="40" />
    <line x1="120" y1="40" x2="180" y2="40" />
  </svg>
);

const EyeFrame1 = () => (
  // SLIT — thin ellipse pupils, short spike lashes
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
    <ellipse cx="50" cy="40" rx="22" ry="4" />
    <circle cx="50" cy="40" r="2" fill="#000" />
    <line x1="32" y1="33" x2="30" y2="28" /><line x1="42" y1="32" x2="40" y2="27" />
    <line x1="58" y1="32" x2="60" y2="27" /><line x1="68" y1="33" x2="70" y2="28" />
    <ellipse cx="150" cy="40" rx="22" ry="4" />
    <circle cx="150" cy="40" r="2" fill="#000" />
    <line x1="132" y1="33" x2="130" y2="28" /><line x1="142" y1="32" x2="140" y2="27" />
    <line x1="158" y1="32" x2="160" y2="27" /><line x1="168" y1="33" x2="170" y2="28" />
  </svg>
);

const EyeFrame2 = () => (
  // HALF OPEN — almond shape, iris+pupil, medium spikes
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
    <path d="M25 40 Q37 26 50 25 Q63 26 75 40 Q63 52 50 53 Q37 52 25 40Z" />
    <circle cx="50" cy="40" r="8" stroke="#000" strokeWidth="1.5" />
    <circle cx="50" cy="40" r="4" fill="#000" />
    <line x1="30" y1="28" x2="27" y2="20" /><line x1="40" y1="24" x2="38" y2="16" />
    <line x1="60" y1="24" x2="62" y2="16" /><line x1="70" y1="28" x2="73" y2="20" />
    <line x1="35" y1="52" x2="33" y2="58" /><line x1="65" y1="52" x2="67" y2="58" />
    <path d="M125 40 Q137 26 150 25 Q163 26 175 40 Q163 52 150 53 Q137 52 125 40Z" />
    <circle cx="150" cy="40" r="8" stroke="#000" strokeWidth="1.5" />
    <circle cx="150" cy="40" r="4" fill="#000" />
    <line x1="130" y1="28" x2="127" y2="20" /><line x1="140" y1="24" x2="138" y2="16" />
    <line x1="160" y1="24" x2="162" y2="16" /><line x1="170" y1="28" x2="173" y2="20" />
    <line x1="135" y1="52" x2="133" y2="58" /><line x1="165" y1="52" x2="167" y2="58" />
  </svg>
);

const EyeFrame3 = () => (
  // FULLY OPEN — wide almond, large iris, heavy long spikes, ink splatter
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
    <path d="M22 40 Q34 20 50 18 Q66 20 78 40 Q66 58 50 60 Q34 58 22 40Z" />
    <circle cx="50" cy="40" r="11" stroke="#000" strokeWidth="1.5" />
    <circle cx="50" cy="40" r="5.5" fill="#000" />
    <circle cx="48" cy="38" r="1.5" fill="#fff" />
    <line x1="28" y1="24" x2="24" y2="12" /><line x1="38" y1="19" x2="35" y2="8" />
    <line x1="50" y1="17" x2="50" y2="6" /><line x1="62" y1="19" x2="65" y2="8" />
    <line x1="72" y1="24" x2="76" y2="12" />
    <line x1="32" y1="56" x2="29" y2="66" /><line x1="50" y1="61" x2="50" y2="70" />
    <line x1="68" y1="56" x2="71" y2="66" />
    <circle cx="35" cy="68" r="1" fill="#000" /><circle cx="42" cy="72" r="0.8" fill="#000" />
    <circle cx="60" cy="70" r="1.2" fill="#000" />
    <path d="M122 40 Q134 20 150 18 Q166 20 178 40 Q166 58 150 60 Q134 58 122 40Z" />
    <circle cx="150" cy="40" r="11" stroke="#000" strokeWidth="1.5" />
    <circle cx="150" cy="40" r="5.5" fill="#000" />
    <circle cx="148" cy="38" r="1.5" fill="#fff" />
    <line x1="128" y1="24" x2="124" y2="12" /><line x1="138" y1="19" x2="135" y2="8" />
    <line x1="150" y1="17" x2="150" y2="6" /><line x1="162" y1="19" x2="165" y2="8" />
    <line x1="172" y1="24" x2="176" y2="12" />
    <line x1="132" y1="56" x2="129" y2="66" /><line x1="150" y1="61" x2="150" y2="70" />
    <line x1="168" y1="56" x2="171" y2="66" />
    <circle cx="140" cy="69" r="1" fill="#000" /><circle cx="158" cy="71" r="0.8" fill="#000" />
  </svg>
);

const EyeFrame4 = () => (
  // GLITCH — eyes at different heights/sizes, chaotic spikes, ink drip
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
    {/* Left eye — shifted down, smaller */}
    <path d="M26 46 Q36 32 48 31 Q60 32 70 46 Q60 58 48 59 Q36 58 26 46Z" />
    <circle cx="48" cy="46" r="8" stroke="#000" strokeWidth="1.5" />
    <circle cx="48" cy="46" r="4" fill="#000" />
    <line x1="30" y1="32" x2="25" y2="22" /><line x1="48" y1="30" x2="46" y2="18" />
    <line x1="66" y1="32" x2="72" y2="24" />
    <line x1="34" y1="58" x2="30" y2="68" /><line x1="58" y1="58" x2="62" y2="70" />
    {/* Ink drip from left eye */}
    <path d="M42 60 Q41 66 42 74" strokeWidth="1.5" />
    <circle cx="42" cy="76" r="1.5" fill="#000" />
    {/* Right eye — shifted up, larger */}
    <path d="M118 34 Q132 16 152 14 Q172 16 182 34 Q172 50 152 52 Q132 50 118 34Z" />
    <circle cx="152" cy="34" r="13" stroke="#000" strokeWidth="1.5" />
    <circle cx="152" cy="34" r="6" fill="#000" />
    <circle cx="150" cy="32" r="2" fill="#fff" />
    <line x1="124" y1="20" x2="118" y2="8" /><line x1="140" y1="14" x2="137" y2="2" />
    <line x1="152" y1="13" x2="154" y2="0" /><line x1="164" y1="14" x2="168" y2="4" />
    <line x1="178" y1="20" x2="184" y2="10" />
    <line x1="130" y1="50" x2="126" y2="60" /><line x1="170" y1="50" x2="176" y2="58" />
    {/* Extra chaotic spikes */}
    <line x1="145" y1="12" x2="142" y2="4" strokeWidth="1" />
    <line x1="160" y1="13" x2="163" y2="3" strokeWidth="1" />
    <circle cx="132" cy="62" r="0.8" fill="#000" /><circle cx="172" cy="60" r="1" fill="#000" />
  </svg>
);

const EyeFrame5 = () => (
  // CLOSING — lashes down, upper lid crushing, slit
  <svg viewBox="0 0 200 80" width="200" height="80" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
    <path d="M28 40 Q38 36 50 35 Q62 36 72 40" />
    <line x1="50" y1="36" x2="50" y2="40" strokeWidth="1" />
    <line x1="32" y1="40" x2="28" y2="48" /><line x1="42" y1="40" x2="40" y2="50" />
    <line x1="58" y1="40" x2="60" y2="50" /><line x1="68" y1="40" x2="72" y2="48" />
    <line x1="36" y1="36" x2="34" y2="32" /><line x1="64" y1="36" x2="66" y2="32" />
    <path d="M128 40 Q138 36 150 35 Q162 36 172 40" />
    <line x1="150" y1="36" x2="150" y2="40" strokeWidth="1" />
    <line x1="132" y1="40" x2="128" y2="48" /><line x1="142" y1="40" x2="140" y2="50" />
    <line x1="158" y1="40" x2="160" y2="50" /><line x1="168" y1="40" x2="172" y2="48" />
    <line x1="136" y1="36" x2="134" y2="32" /><line x1="164" y1="36" x2="166" y2="32" />
  </svg>
);

const eyeFrames = [EyeFrame0, EyeFrame1, EyeFrame2, EyeFrame3, EyeFrame4, EyeFrame5];
const cycleSequence = [0, 1, 2, 3, 4, 3, 4, 3, 5, 1, 0];
const loadSteps = [4, 4, 6, 3, 8, 5, 7, 4, 9, 6, 5, 8, 4, 6, 5, 7, 3, 9, 5];

const LoadingScreen: React.FC = () => {
  const [pct, setPct] = useState(0);
  const [activeFrame, setActiveFrame] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [hidden, setHidden] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const pctRef = useRef(0);
  const stepIdxRef = useRef(0);
  const cycleIdxRef = useRef(0);
  const loadedRef = useRef(false);

  const finishLoad = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    // Exit sequence: hold open → close → shut → exit
    setActiveFrame(3);
    setTimeout(() => setActiveFrame(5), 400);
    setTimeout(() => setActiveFrame(0), 600);
    setTimeout(() => setExiting(true), 780);
    setTimeout(() => {
      setHidden(true);
      document.body.classList.add('loaded');
    }, 1100);
  }, []);

  useEffect(() => {
    // Real window load listener
    const onLoad = () => {
      pctRef.current = 100;
      setPct(100);
    };

    if (document.readyState === 'complete') {
      // Already loaded — still show the animation briefly
      setTimeout(onLoad, 600);
    } else {
      window.addEventListener('load', onLoad);
    }

    // Fake progress tick
    const tick = () => {
      if (loadedRef.current) return;

      // Advance progress
      if (pctRef.current < 100) {
        const step = loadSteps[stepIdxRef.current % loadSteps.length];
        stepIdxRef.current++;
        pctRef.current = Math.min(pctRef.current + step, loadedRef.current ? 100 : 92);
        setPct(pctRef.current);
      }

      // If real load happened, jump to 100
      if (pctRef.current >= 100) {
        setPct(100);
        finishLoad();
        return;
      }

      // Cycle eye frames
      cycleIdxRef.current = (cycleIdxRef.current + 1) % cycleSequence.length;
      setActiveFrame(cycleSequence[cycleIdxRef.current]);

      // Uneven timing — blinks faster as load progresses
      const p = pctRef.current;
      const delay = p < 40 ? 220 : p < 75 ? 160 : p < 90 ? 120 : 80;
      setTimeout(tick, delay);
    };

    const initialDelay = setTimeout(tick, 300);

    return () => {
      clearTimeout(initialDelay);
      window.removeEventListener('load', onLoad);
    };
  }, [finishLoad]);

  // When pct hits 100, trigger finish
  useEffect(() => {
    if (pct >= 100 && !loadedRef.current) {
      finishLoad();
    }
  }, [pct, finishLoad]);

  if (hidden) return null;

  const padded = String(Math.floor(pct)).padStart(3, '0');

  return (
    <div ref={loaderRef} className={`loader${exiting ? ' exiting' : ''}`}>
      {/* Corner crosshairs */}
      <div className="loader-xhair tl" />
      <div className="loader-xhair tr" />
      <div className="loader-xhair bl" />
      <div className="loader-xhair br" />

      {/* Eyes */}
      <div className="loader-eyes">
        {eyeFrames.map((Frame, i) => (
          <div key={i} className={`loader-eye-frame${i === activeFrame ? ' active' : ''}`}>
            <Frame />
          </div>
        ))}
      </div>

      {/* Label */}
      <div className="loader-label">
        [ loading <span className="red">andrxw</span> // please wait ]
      </div>

      {/* Progress */}
      <div className="loader-progress">
        <div className="loader-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      {/* Counter */}
      <div className="loader-counter">{padded} // 100</div>

      {/* Serial & version */}
      <span className="loader-serial">SRL // RO-MK-001</span>
      <span className="loader-version">VER. <span className="red">2.0</span></span>
    </div>
  );
};

export default LoadingScreen;
